import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { companiesService } from "@/api/companies";
import { OnboardingQuestions, type TOnboardingCompletionResult } from "@/components/onboarding/OnboardingQuestions";
import { useToast } from "@/components/ui";
import { type TBoundedPoller, createBoundedPoller } from "@/lib/boundedPoller";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";

const POLL_INTERVAL_MS = 3000;
// Summary generation is a multi-tool LLM call and routinely runs past two minutes.
const POLL_TIMEOUT_MS = 240_000;
// The chat holds for at least this long after completion so the AI's closing message and
// the progress bubble are actually readable before the page swaps.
const MIN_DWELL_MS = 4000;
// ...but never longer than this while still generating — the summary page has
// its own honest processing UI and keeps polling, so a long run hands off early.
const MAX_DWELL_MS = 15_000;

/**
 * Onboarding page component that wraps OnboardingQuestions.
 *
 * It is the single owner of post-completion navigation. After the WebSocket signals
 * completion it:
 *  1. Triggers summary generation via REST
 *  2. Polls status, feeding stage/elapsed into the in-chat progress bubble
 *  3. Navigates to the summary page once generation settles — never before MIN_DWELL_MS
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { cancelOnboarding, completeOnboarding } = useChatStore(
    useShallow((state) => ({
      cancelOnboarding: state.cancelOnboarding,
      completeOnboarding: state.completeOnboarding,
    }))
  );

  const invalidateCompanies = useStore((state) => state.invalidateCompanies);
  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const fetchOnboardingSummary = useStore((state) => state.fetchOnboardingSummary);

  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [summaryProgress, setSummaryProgress] = useState<{
    stage?: string | null;
    elapsedSeconds?: number | null;
  }>({});
  const pollerRef = useRef<TBoundedPoller | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processingStartedAtRef = useRef<number | null>(null);
  const hasNavigatedRef = useRef(false);

  const stopPolling = useCallback(() => {
    pollerRef.current?.stop();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
    pollerRef.current = null;
    timeoutRef.current = null;
    navigateTimerRef.current = null;
  }, []);

  // Clean up on unmount — an in-flight interval used to outlive the page.
  useEffect(() => stopPolling, [stopPolling]);

  /**
   * The only path to the summary page. Idempotent, and always leaves the closing message
   * on screen for at least MIN_DWELL_MS.
   */
  const requestNavigate = useCallback(
    (companyId: string) => {
      if (hasNavigatedRef.current) return;
      hasNavigatedRef.current = true;
      stopPolling();

      const startedAt = processingStartedAtRef.current ?? Date.now();
      const delay = Math.max(0, MIN_DWELL_MS - (Date.now() - startedAt));

      navigateTimerRef.current = setTimeout(() => {
        invalidateCompanies();
        void fetchCompanies({ force: true }).then(() => {
          void navigate({
            to: "/app/companies/$company_id/onboarding/summary",
            params: { company_id: companyId },
            replace: true,
          });
        });
      }, delay);
    },
    [stopPolling, invalidateCompanies, fetchCompanies, navigate]
  );

  const handleOnboardingComplete = useCallback(
    ({ history, companyId: completedCompanyId }: TOnboardingCompletionResult) => {
      const projectName =
        history.find((entry) => entry.answer.text && entry.answer.text.trim().length > 0)?.answer.text?.trim() ??
        undefined;

      completeOnboarding({ projectName, companyId: completedCompanyId });
      setSummaryGenerating(true);
      processingStartedAtRef.current = Date.now();
      hasNavigatedRef.current = false;

      void (async () => {
        // Trigger summary generation
        const { error } = await companiesService.completeOnboarding(completedCompanyId);
        if (error) {
          toast({
            variant: "default",
            title: "Almost there",
            description:
              "Your answers were saved, but we encountered an issue starting the analysis. The summary page will show your status and let you retry.",
          });
          requestNavigate(completedCompanyId);
          return;
        }

        // Poll until summary is ready, failed, or the dwell cap — lite fetches
        // only, so a 3s cadence stays cheap on the backend.
        const startedAt = Date.now();

        stopPolling();
        pollerRef.current = createBoundedPoller({
          intervalMs: POLL_INTERVAL_MS,
          maxDurationMs: POLL_TIMEOUT_MS,
          tick: async () => {
            const summary = await fetchOnboardingSummary(completedCompanyId, { force: true, lite: true });
            if (!summary) return true; // transient fetch miss — keep polling

            setSummaryProgress({
              stage: summary.current_stage ?? null,
              elapsedSeconds: summary.elapsed_seconds ?? null,
            });

            if (summary.status !== "processing" || Date.now() - startedAt >= MAX_DWELL_MS) {
              requestNavigate(completedCompanyId);
              return false;
            }
            // status === "processing" → keep polling, keep the user in the chat
            return true;
          },
        });
        pollerRef.current.start();

        // Hard fallback — the summary page keeps polling from there. (Also covers
        // a hidden tab, where poller ticks are intentionally paused.)
        timeoutRef.current = setTimeout(() => {
          requestNavigate(completedCompanyId);
        }, MAX_DWELL_MS);
      })();
    },
    [completeOnboarding, requestNavigate, stopPolling, fetchOnboardingSummary, toast]
  );

  const handleCancel = useCallback(() => {
    cancelOnboarding();
    void navigate({ to: "/app" });
  }, [cancelOnboarding, navigate]);

  return (
    <OnboardingQuestions
      onCancel={handleCancel}
      onComplete={handleOnboardingComplete}
      summaryGenerating={summaryGenerating}
      summaryProgress={summaryProgress}
    />
  );
}
